import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/**
 * useState persisted to localStorage as JSON under `key`.
 *
 * Reads lazily on mount; writes on every change. All storage access is
 * wrapped in try/catch — test environments may stub `window.localStorage`
 * with a partial object, SSR has no `window` at all, and writes can throw
 * on quota/blocked storage. Any failure falls back to `defaultValue` (reads)
 * or drops silently (writes).
 *
 * `sanitize` validates/clamps the parsed raw value; returning `undefined`
 * rejects it and falls back to `defaultValue`. Without it the parsed JSON
 * is trusted as `T` — pass one whenever the shape matters.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  sanitize?: (raw: unknown) => T | undefined,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      if (typeof window === "undefined") return defaultValue;
      const raw = window.localStorage?.getItem?.(key);
      if (raw == null) return defaultValue;
      const parsed: unknown = JSON.parse(raw);
      if (sanitize) {
        const cleaned = sanitize(parsed);
        return cleaned === undefined ? defaultValue : cleaned;
      }
      return parsed as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage?.setItem?.(key, JSON.stringify(value));
    } catch {
      // Quota exceeded / blocked storage / test stub — drop silently.
    }
  }, [key, value]);

  return [value, setValue];
}
